import * as React from "react";
import { useRouterState } from "@tanstack/react-router";
import { cn } from "@/shared/lib/cn";
import type { NavigationItem } from "./navigation";
import { getIcon, withTooltip } from "./navigationUtils";
import { NavigationItemContent } from "./NavigationItemContent";
import { NavigationItemWithSubmenu } from "./NavigationItemWithSubmenu";
import { NavigationItemLink } from "./NavigationItemLink";

interface NavigationItemCardProps {
  item: NavigationItem;
  collapsed: boolean;
  onNavigate?: () => void;
}

export function NavigationItemCard({
  item,
  collapsed,
  onNavigate,
}: NavigationItemCardProps) {
  const isAdminRoute = item.target.to === "/admin";
  const iconNode = getIcon(item);

  const currentAdminPanel = useRouterState({
    select: (state) => {
      if (state.location.pathname === "/admin") {
        return (state.location.search as { panel?: string })?.panel;
      }
      return undefined;
    },
  });

  const isActive = React.useMemo(() => {
    if (!item.children || item.children.length === 0) {
      return false;
    }

    if (isAdminRoute && currentAdminPanel) {
      return item.children.some(
        (child) => child.target.search?.panel === currentAdminPanel
      );
    }

    return false;
  }, [item.children, isAdminRoute, currentAdminPanel]);

  const baseClasses =
    "group relative flex items-center gap-3 overflow-hidden rounded-2xl px-3 py-2 text-sm transition-all duration-200";
  const collapseAdjustment = collapsed ? "justify-center px-2" : "";

  if (item.disabled) {
    const card = (
      <div
        key={item.id}
        aria-disabled="true"
        className={cn(
          baseClasses,
          "border border-white/12 bg-white/5 text-white/55 backdrop-blur",
          "focus:outline-none focus-visible:outline-none",
          collapseAdjustment
        )}
      >
        <NavigationItemContent
          collapsed={collapsed}
          item={item}
          iconNode={iconNode}
        />
      </div>
    );

    return withTooltip(collapsed, item, card);
  }

  if (item.children && item.children.length > 0) {
    return (
      <NavigationItemWithSubmenu
        item={item}
        collapsed={collapsed}
        onNavigate={onNavigate}
        iconNode={iconNode}
        isActive={isActive}
      />
    );
  }

  return (
    <NavigationItemLink
      item={item}
      collapsed={collapsed}
      onNavigate={onNavigate}
      iconNode={iconNode}
    />
  );
}
